import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Store, MonitorPlay, Wrench, Percent, FileText, Users, ScrollText,
  Plus, Trash2, Save, Power, Settings as SettingsIcon, Coins, BarChart3,
} from "lucide-react";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { Navbar, Footer } from "@/components/Chrome";
import { useAuth, startLogin } from "@/context/AuthContext";
import { api, money2, num, STATUS_LABEL, STATUS_COLOR, REDEMPTION_STATUS } from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [
  { id: "kpis", label: "KPIs", icon: LayoutDashboard },
  { id: "analytics", label: "Analítica", icon: BarChart3 },
  { id: "quotes", label: "Cotizaciones", icon: FileText },
  { id: "leads", label: "Leads", icon: Users },
  { id: "redemptions", label: "Canjes", icon: Coins },
  { id: "spaces", label: "Espacios", icon: MonitorPlay },
  { id: "retailers", label: "Retailers", icon: Store },
  { id: "services", label: "Servicios", icon: Wrench },
  { id: "discounts", label: "Descuentos", icon: Percent },
  { id: "settings", label: "Fee y promos", icon: SettingsIcon },
  { id: "audit", label: "Auditoría", icon: ScrollText },
];

const STATES = ["borrador", "enviada", "en_negociacion", "aprobada", "rechazada", "expirada"];

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-spott-mid bg-white ${className}`}>{children}</div>
);

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-spott-blue">{label}</Label>
    {children}
  </div>
);

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("kpis");
  const [kpis, setKpis] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [services, setServices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [noteModal, setNoteModal] = useState(null);
  const [note, setNote] = useState("");
  const [newSpace, setNewSpace] = useState(null);
  const [newRetailer, setNewRetailer] = useState(null);
  const [newService, setNewService] = useState(null);

  const isStaff = user && (user.role === "admin" || user.role === "agente");

  const load = useCallback(async () => {
    if (!isStaff) return;
    const [k, q, l, s, r, sv, d, a, cfg, rd] = await Promise.all([
      api.get("/admin/kpis"), api.get("/admin/quotes"), api.get("/admin/leads"),
      api.get("/admin/spaces"), api.get("/admin/retailers"), api.get("/admin/services"),
      api.get("/admin/discounts"), api.get("/admin/audit"), api.get("/admin/settings"),
      api.get("/admin/redemptions"),
    ]);
    setKpis(k.data); setQuotes(q.data); setLeads(l.data); setSpaces(s.data);
    setRetailers(r.data); setServices(sv.data); setDiscounts(d.data); setAudit(a.data);
    setSettings(cfg.data); setRedemptions(rd.data);
    if (user.role === "admin") {
      try { setUsers((await api.get("/admin/users")).data); } catch {}
    }
  }, [isStaff, user]);

  useEffect(() => { document.title = "Panel Spott"; }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (quoteId, status, noteText = "") => {
    await api.put(`/admin/quotes/${quoteId}/status`, { status, note: noteText });
    toast.success("Estado actualizado");
    load();
  };

  const saveItem = async (path, idField, item) => {
    const body = { ...item };
    delete body[idField];
    await api.put(`/admin/${path}/${item[idField]}`, body);
    toast.success("Guardado");
    load();
  };

  const del = async (path, id) => {
    await api.delete(`/admin/${path}/${id}`);
    toast.success("Eliminado");
    load();
  };

  const create = async (path, body) => {
    try {
      await api.post(`/admin/${path}`, body);
      toast.success("Creado");
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
      return false;
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-spott-light"><Navbar />
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-spott-yellow border-t-transparent" />
      </div>
    </div>
  );

  if (!isStaff) return (
    <div className="min-h-screen bg-spott-light"><Navbar />
      <div className="mx-auto max-w-md px-5 py-28 text-center" data-testid="admin-gate">
        <h1 className="text-2xl font-bold text-spott-blue">Panel interno de Spott</h1>
        <p className="mt-3 text-sm text-gray-600">
          {user ? "Tu cuenta no tiene permisos de administración." : "Inicia sesión con una cuenta del equipo Spott."}
        </p>
        {!user && (
          <button onClick={() => startLogin("/admin")} data-testid="admin-login-btn"
            className="mt-8 w-full rounded-full bg-spott-yellow py-4 text-sm font-bold text-spott-blue">
            Continuar con Google
          </button>
        )}
      </div>
      <Footer />
    </div>
  );

  const filteredQuotes = statusFilter ? quotes.filter((q) => q.status === statusFilter) : quotes;

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="relative overflow-hidden bg-spott-blue py-12 grain">
        <div className="arc-deco -left-20 -bottom-32 h-80 w-80" />
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Backoffice</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">Panel de administración</h1>
          <p className="mt-2 text-sm font-light text-spott-mid">{user.email} · {user.role}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-10">
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-spott-blue text-white" : "border border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
              }`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "analytics" && <AdminAnalytics />}

        {/* KPIS */}
        {tab === "kpis" && kpis && (
          <div className="mt-8" data-testid="admin-kpis">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { l: "Cotizaciones generadas", v: kpis.total_quotes },
                { l: "Leads comerciales", v: kpis.leads },
                { l: "Tasa de conversión", v: `${kpis.conversion_rate}%` },
                { l: "Pipeline potencial", v: money2(kpis.pipeline_value) },
                { l: "Ticket promedio", v: money2(kpis.avg_ticket) },
                { l: "Valor aprobado", v: money2(kpis.won_value) },
                { l: "Cotizaciones enviadas", v: kpis.sent_quotes },
                { l: "Espacios activos", v: spaces.filter((s) => s.active).length },
              ].map((k) => (
                <Card key={k.l} className="p-6">
                  <div className="text-xs text-gray-500">{k.l}</div>
                  <div className="mt-2 text-2xl font-bold text-spott-yellow">{k.v}</div>
                </Card>
              ))}
            </div>
            <div className="mt-6 grid gap-5 lg:grid-cols-3">
              <Card className="p-6">
                <h3 className="text-sm font-bold text-spott-blue">Embudo por estado</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {Object.entries(kpis.by_status).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-gray-600">
                      <span>{STATUS_LABEL[k]}</span><span className="font-semibold text-spott-blue">{v}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="text-sm font-bold text-spott-blue">Espacios más cotizados</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {kpis.top_spaces.length === 0 && <li className="text-gray-500">Sin datos aún</li>}
                  {kpis.top_spaces.map((s) => (
                    <li key={s.name} className="flex justify-between gap-3 text-gray-600">
                      <span className="truncate">{s.name}</span><span className="font-semibold text-spott-blue">{s.count}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="text-sm font-bold text-spott-blue">Retailers con más demanda</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {kpis.top_retailers.length === 0 && <li className="text-gray-500">Sin datos aún</li>}
                  {kpis.top_retailers.map((s) => (
                    <li key={s.name} className="flex justify-between text-gray-600">
                      <span>{s.name}</span><span className="font-semibold text-spott-blue">{s.count}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
            {user.role === "admin" && (
              <Card className="mt-6 p-6">
                <h3 className="text-sm font-bold text-spott-blue">Usuarios y roles</h3>
                <div className="mt-4 space-y-3" data-testid="admin-users">
                  {users.map((u) => (
                    <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-spott-mid pb-3">
                      <div>
                        <div className="text-sm font-medium text-spott-blue">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <select value={u.role} data-testid={`user-role-${u.user_id}`}
                        onChange={async (e) => { await api.put(`/admin/users/${u.user_id}/role`, { role: e.target.value }); toast.success("Rol actualizado"); load(); }}
                        className="h-9 rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                        {["cliente", "agente", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* QUOTES */}
        {tab === "quotes" && (
          <div className="mt-8" data-testid="admin-quotes">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-status-filter"
              className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
              <option value="">Todos los estados</option>
              {STATES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <div className="mt-5 space-y-4">
              {filteredQuotes.map((q) => (
                <Card key={q.quote_id} className="p-6" data-testid={`admin-quote-${q.quote_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-xs text-gray-500">{q.quote_id}</span>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLOR[q.status]}`}>
                          {STATUS_LABEL[q.status]}
                        </span>
                        {!q.user_id && <span className="rounded-full bg-spott-light px-3 py-1 text-xs text-gray-500">visitante</span>}
                      </div>
                      <h3 className="mt-2 text-base font-semibold text-spott-blue">{q.company_name || "Sin marca"}</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        {q.contact_name || "—"} · {q.contact_email || "—"} · {q.space_items?.length || 0} espacios ·
                        {" "}{q.duration_months} mes(es) · {num(q.total_reach)} impactos
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-spott-blue">{money2(q.total)}</div>
                      <Link to={`/cotizacion/${q.quote_id}`} className="text-xs font-semibold text-spott-blue underline"
                        data-testid={`admin-quote-view-${q.quote_id}`}>Ver detalle</Link>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-spott-mid pt-4">
                    <select value={q.status} onChange={(e) => setStatus(q.quote_id, e.target.value)}
                      data-testid={`admin-quote-status-${q.quote_id}`}
                      className="h-9 rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                      {STATES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                    <button onClick={() => { setNoteModal(q.quote_id); setNote(""); }}
                      data-testid={`admin-quote-note-${q.quote_id}`}
                      className="rounded-full border border-spott-mid px-4 py-2 text-xs font-semibold text-spott-blue">
                      Agregar nota
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* LEADS */}
        {tab === "leads" && (
          <div className="mt-8 space-y-4" data-testid="admin-leads">
            {leads.length === 0 && <Card className="p-10 text-center text-sm text-gray-600">Aún no hay leads.</Card>}
            {leads.map((l) => (
              <Card key={l.lead_id} className="p-6" data-testid={`admin-lead-${l.lead_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-spott-blue">{l.contact_name}</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {l.contact_email} · {l.contact_phone || "sin teléfono"} · {l.company_name || "—"}
                    </p>
                    {l.message && <p className="mt-3 border-l-2 border-spott-yellow pl-3 text-sm text-gray-600">{l.message}</p>}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-spott-blue">{money2(l.total)}</div>
                    {l.quote_id ? (
                      <Link to={`/cotizacion/${l.quote_id}`} className="text-xs font-semibold text-spott-blue underline">
                        {l.quote_id}
                      </Link>
                    ) : (
                      <div className="text-xs font-semibold text-spott-yellow">Propuesta 360 solicitada</div>
                    )}
                    {(l.objective || l.budget_range) && (
                      <div className="mt-1 text-xs text-gray-500">
                        {[l.objective, l.budget_range].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-500">{STATUS_LABEL[l.funnel_status] || l.funnel_status}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* REDEMPTIONS */}
        {tab === "redemptions" && (
          <div className="mt-8 space-y-4" data-testid="admin-redemptions">
            {redemptions.length === 0 && (
              <Card className="p-10 text-center text-sm text-gray-600">Aún no hay solicitudes de canje.</Card>
            )}
            {redemptions.map((r) => (
              <Card key={r.redemption_id} className="p-6" data-testid={`admin-redemption-${r.redemption_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${REDEMPTION_STATUS[r.status].cls}`}>
                        {REDEMPTION_STATUS[r.status].label}
                      </span>
                      <span className="font-mono text-xs text-gray-500">{r.redemption_id}</span>
                    </div>
                    <h3 className="mt-2 text-base font-semibold text-spott-blue">
                      {r.units}× {r.space_name} · {r.months} mes(es)
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {r.retailer_name} · {r.city} · {r.user_name} ({r.user_email})
                      {r.company_name ? ` · ${r.company_name}` : ""}
                    </p>
                    {r.note && (
                      <p className="mt-3 border-l-2 border-spott-yellow pl-3 text-sm text-gray-600">{r.note}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1.5 text-xl font-bold text-spott-blue">
                      <Coins size={16} className="text-spott-yellow" /> {num(r.points_cost)}
                    </div>
                    <div className="text-xs text-gray-500">valor {money2(r.value_usd)}</div>
                  </div>
                </div>
                <div className="mt-4 border-t border-spott-mid pt-4">
                  <select value={r.status} data-testid={`redemption-status-${r.redemption_id}`}
                    onChange={async (e) => {
                      await api.put(`/admin/redemptions/${r.redemption_id}/status`, { status: e.target.value });
                      toast.success("Canje actualizado");
                      load();
                    }}
                    className="h-9 rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                    {Object.entries(REDEMPTION_STATUS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* SPACES */}
        {tab === "spaces" && (
          <div className="mt-8" data-testid="admin-spaces">
            <button onClick={() => setNewSpace({
              retailer_id: retailers[0]?.retailer_id || "", name: "", city: "", format: "",
              funnel_stage: "conversion", format_specs: "",
              reach_monthly: 0, price_month: 0, nse: [], images: [], objectives: [], description: "",
            })} data-testid="admin-new-space-btn"
              className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-5 py-2.5 text-sm font-bold text-spott-blue">
              <Plus size={15} /> Nuevo espacio
            </button>
            <div className="mt-5 space-y-3">
              {spaces.map((s, idx) => (
                <Card key={s.space_id} className="p-5" data-testid={`admin-space-${s.space_id}`}>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <Row label="Nombre">
                      <Input value={s.name} onChange={(e) => {
                        const n = [...spaces]; n[idx] = { ...s, name: e.target.value }; setSpaces(n);
                      }} data-testid={`space-name-${s.space_id}`} />
                    </Row>
                    <Row label="Ciudad">
                      <Input value={s.city} onChange={(e) => {
                        const n = [...spaces]; n[idx] = { ...s, city: e.target.value }; setSpaces(n);
                      }} />
                    </Row>
                    <Row label="Formato">
                      <Input value={s.format} onChange={(e) => {
                        const n = [...spaces]; n[idx] = { ...s, format: e.target.value }; setSpaces(n);
                      }} />
                    </Row>
                    <Row label="Alcance/mes">
                      <Input type="number" value={s.reach_monthly} onChange={(e) => {
                        const n = [...spaces]; n[idx] = { ...s, reach_monthly: Number(e.target.value) }; setSpaces(n);
                      }} />
                    </Row>
                    <Row label="Precio/mes (USD)">
                      <Input type="number" value={s.price_month} data-testid={`space-price-${s.space_id}`}
                        onChange={(e) => {
                          const n = [...spaces]; n[idx] = { ...s, price_month: Number(e.target.value) }; setSpaces(n);
                        }} />
                    </Row>
                    <Row label="Etapa del embudo">
                      <select value={s.funnel_stage || "conversion"} data-testid={`space-stage-${s.space_id}`}
                        onChange={(e) => {
                          const n = [...spaces]; n[idx] = { ...s, funnel_stage: e.target.value }; setSpaces(n);
                        }}
                        className="h-10 w-full rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                        <option value="awareness">Awareness</option>
                        <option value="conversion">Conversión</option>
                      </select>
                    </Row>
                    <Row label="Especificaciones del formato digital">
                      <Input value={s.format_specs || ""} data-testid={`space-specs-${s.space_id}`}
                        onChange={(e) => {
                          const n = [...spaces];
                          n[idx] = { ...s, format_specs: e.target.value, is_digital: !!e.target.value };
                          setSpaces(n);
                        }} />
                    </Row>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="mr-auto text-xs text-gray-500">{s.retailer_name}</span>
                    <button onClick={() => saveItem("spaces", "space_id", s)} data-testid={`space-save-${s.space_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-spott-blue px-4 py-2 text-xs font-bold text-white">
                      <Save size={13} /> Guardar
                    </button>
                    <button onClick={() => saveItem("spaces", "space_id", { ...s, available: !s.available })}
                      data-testid={`space-avail-${s.space_id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold ${
                        s.available ? "bg-emerald-600 text-white" : "bg-spott-mid text-spott-blue"
                      }`}>
                      <Power size={13} /> {s.available ? "Disponible" : "No disponible"}
                    </button>
                    <button onClick={() => saveItem("spaces", "space_id", { ...s, active: !s.active })}
                      className="rounded-full border border-spott-mid px-4 py-2 text-xs font-semibold text-spott-blue">
                      {s.active ? "Desactivar" : "Activar"}
                    </button>
                    <button onClick={() => del("spaces", s.space_id)} data-testid={`space-delete-${s.space_id}`}
                      className="rounded-full border border-red-200 p-2 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* RETAILERS */}
        {tab === "retailers" && (
          <div className="mt-8" data-testid="admin-retailers">
            <button onClick={() => setNewRetailer({ name: "", category: "supermercado", cities: [], logo_text: "", logo_url: "", description: "" })}
              data-testid="admin-new-retailer-btn"
              className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-5 py-2.5 text-sm font-bold text-spott-blue">
              <Plus size={15} /> Nuevo retailer
            </button>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {retailers.map((r, idx) => (
                <Card key={r.retailer_id} className="p-5" data-testid={`admin-retailer-${r.retailer_id}`}>
                  <div className="grid gap-3">
                    <Row label="Nombre">
                      <Input value={r.name} onChange={(e) => {
                        const n = [...retailers]; n[idx] = { ...r, name: e.target.value }; setRetailers(n);
                      }} />
                    </Row>
                    <Row label="Categoría">
                      <Input value={r.category} onChange={(e) => {
                        const n = [...retailers]; n[idx] = { ...r, category: e.target.value }; setRetailers(n);
                      }} />
                    </Row>
                    <Row label="Ciudades (separadas por coma)">
                      <Input value={(r.cities || []).join(", ")} onChange={(e) => {
                        const n = [...retailers];
                        n[idx] = { ...r, cities: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) };
                        setRetailers(n);
                      }} />
                    </Row>
                    <Row label="URL del logo">
                      <Input value={r.logo_url || ""} data-testid={`retailer-logo-url-${r.retailer_id}`}
                        onChange={(e) => {
                          const n = [...retailers]; n[idx] = { ...r, logo_url: e.target.value }; setRetailers(n);
                        }} />
                    </Row>
                    {r.logo_url && (
                      <img src={r.logo_url} alt={`Logo de ${r.name}`}
                        className="h-16 w-auto self-start object-contain" />
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => saveItem("retailers", "retailer_id", r)}
                      data-testid={`retailer-save-${r.retailer_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-spott-blue px-4 py-2 text-xs font-bold text-white">
                      <Save size={13} /> Guardar
                    </button>
                    <button onClick={() => del("retailers", r.retailer_id)}
                      className="rounded-full border border-red-200 p-2 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* SERVICES */}
        {tab === "services" && (
          <div className="mt-8" data-testid="admin-services">
            <button onClick={() => setNewService({ name: "", description: "", price_type: "fixed", price: 0 })}
              data-testid="admin-new-service-btn"
              className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-5 py-2.5 text-sm font-bold text-spott-blue">
              <Plus size={15} /> Nuevo servicio
            </button>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {services.map((s, idx) => (
                <Card key={s.service_id} className="p-5" data-testid={`admin-service-${s.service_id}`}>
                  <div className="grid gap-3">
                    <Row label="Nombre">
                      <Input value={s.name} onChange={(e) => {
                        const n = [...services]; n[idx] = { ...s, name: e.target.value }; setServices(n);
                      }} />
                    </Row>
                    <Row label="Precio (USD)">
                      <Input type="number" value={s.price} data-testid={`service-price-${s.service_id}`}
                        onChange={(e) => {
                          const n = [...services]; n[idx] = { ...s, price: Number(e.target.value) }; setServices(n);
                        }} />
                    </Row>
                    <Row label="Tipo de precio">
                      <select value={s.price_type} onChange={(e) => {
                        const n = [...services]; n[idx] = { ...s, price_type: e.target.value }; setServices(n);
                      }} className="h-10 w-full rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                        <option value="fixed">Fijo</option>
                        <option value="per_space">Por espacio</option>
                      </select>
                    </Row>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => saveItem("services", "service_id", s)}
                      data-testid={`service-save-${s.service_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-spott-blue px-4 py-2 text-xs font-bold text-white">
                      <Save size={13} /> Guardar
                    </button>
                    <button onClick={() => del("services", s.service_id)}
                      className="rounded-full border border-red-200 p-2 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* DISCOUNTS */}
        {tab === "discounts" && (
          <div className="mt-8" data-testid="admin-discounts">
            <button onClick={() => create("discounts", { min_months: 24, percent: 18 })}
              data-testid="admin-new-discount-btn"
              className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-5 py-2.5 text-sm font-bold text-spott-blue">
              <Plus size={15} /> Nueva regla
            </button>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {discounts.map((d, idx) => (
                <Card key={d.rule_id} className="p-5" data-testid={`admin-discount-${d.rule_id}`}>
                  <Row label="Meses mínimos">
                    <Input type="number" value={d.min_months} onChange={(e) => {
                      const n = [...discounts]; n[idx] = { ...d, min_months: Number(e.target.value) }; setDiscounts(n);
                    }} />
                  </Row>
                  <div className="mt-3">
                    <Row label="Descuento (%)">
                      <Input type="number" value={d.percent} data-testid={`discount-percent-${d.rule_id}`}
                        onChange={(e) => {
                          const n = [...discounts]; n[idx] = { ...d, percent: Number(e.target.value) }; setDiscounts(n);
                        }} />
                    </Row>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => saveItem("discounts", "rule_id", d)}
                      data-testid={`discount-save-${d.rule_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-spott-blue px-4 py-2 text-xs font-bold text-white">
                      <Save size={13} /> Guardar
                    </button>
                    <button onClick={() => del("discounts", d.rule_id)}
                      className="rounded-full border border-red-200 p-2 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && settings && (
          <div className="mt-8 grid gap-5 lg:grid-cols-2" data-testid="admin-settings">
            <Card className="p-6">
              <h3 className="text-sm font-bold text-spott-blue">Pricing y condiciones</h3>
              <div className="mt-4 space-y-4">
                <Row label="Fee de servicio Spott (%)">
                  <Input type="number" value={settings.service_fee_percent} data-testid="settings-fee-percent"
                    onChange={(e) => setSettings({ ...settings, service_fee_percent: Number(e.target.value) })} />
                </Row>
                <Row label="Etiqueta del fee">
                  <Input value={settings.service_fee_label}
                    onChange={(e) => setSettings({ ...settings, service_fee_label: e.target.value })} />
                </Row>
                <Row label="Reportería gratis desde (USD de inversión en espacios)">
                  <Input type="number" value={settings.analytics_free_threshold}
                    data-testid="settings-analytics-threshold"
                    onChange={(e) => setSettings({ ...settings, analytics_free_threshold: Number(e.target.value) })} />
                </Row>
                <Row label="Puntos por dólar ejecutado">
                  <Input type="number" value={settings.points_per_dollar ?? 1} data-testid="settings-points-earn"
                    onChange={(e) => setSettings({ ...settings, points_per_dollar: Number(e.target.value) })} />
                </Row>
                <Row label="Puntos necesarios por dólar al canjear">
                  <Input type="number" value={settings.points_per_dollar_redeem ?? 20}
                    data-testid="settings-points-redeem"
                    onChange={(e) => setSettings({ ...settings, points_per_dollar_redeem: Number(e.target.value) })} />
                </Row>
                <p className="text-xs text-gray-500">
                  Los descuentos por permanencia se muestran siempre como rangos máximos ("hasta X%").
                </p>
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-sm font-bold text-spott-blue">Banner promocional del cotizador</h3>
              <div className="mt-4 space-y-4">
                <label className="flex items-center gap-3 text-sm text-spott-blue">
                  <input type="checkbox" checked={!!settings.promo_active} data-testid="settings-promo-active"
                    onChange={(e) => setSettings({ ...settings, promo_active: e.target.checked })}
                    className="h-4 w-4 accent-[#FFB000]" />
                  Mostrar banner en el cotizador
                </label>
                <Row label="Título">
                  <Input value={settings.promo_title || ""} data-testid="settings-promo-title"
                    onChange={(e) => setSettings({ ...settings, promo_title: e.target.value })} />
                </Row>
                <Row label="Texto">
                  <Input value={settings.promo_text || ""} data-testid="settings-promo-text"
                    onChange={(e) => setSettings({ ...settings, promo_text: e.target.value })} />
                </Row>
                <Row label="Texto del botón">
                  <Input value={settings.promo_cta_label || ""}
                    onChange={(e) => setSettings({ ...settings, promo_cta_label: e.target.value })} />
                </Row>
                <Row label="Clave de acceso al cotizador">
                  <Input value={settings.quote_access_code ?? ""} placeholder="SPOTT2026"
                    data-testid="settings-access-code"
                    onChange={(e) => setSettings({ ...settings, quote_access_code: e.target.value })} />
                </Row>
                <p className="text-xs text-gray-500">
                  Quien entre a <span className="font-semibold">/cotizar</span> deberá ingresar esta clave.
                  Compártela solo con marcas y agencias autorizadas.
                </p>
              </div>
            </Card>
            <div className="lg:col-span-2">
              <button data-testid="settings-save-btn" onClick={async () => {
                await api.put("/admin/settings", {
                  service_fee_percent: settings.service_fee_percent,
                  service_fee_label: settings.service_fee_label,
                  analytics_free_threshold: settings.analytics_free_threshold,
                  points_per_dollar: settings.points_per_dollar,
                  points_per_dollar_redeem: settings.points_per_dollar_redeem,
                  promo_active: settings.promo_active,
                  promo_title: settings.promo_title,
                  promo_text: settings.promo_text,
                  promo_cta_label: settings.promo_cta_label,
                  quote_access_code: settings.quote_access_code,
                });
                toast.success("Ajustes guardados");
                load();
              }}
                className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-6 py-3 text-sm font-bold text-spott-blue">
                <Save size={16} /> Guardar ajustes
              </button>
            </div>
          </div>
        )}

        {/* AUDIT */}
        {tab === "audit" && (
          <Card className="mt-8 overflow-hidden" data-testid="admin-audit">
            {audit.map((a) => (
              <div key={a.log_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-spott-mid px-6 py-3 text-sm">
                <span className="font-medium text-spott-blue">{a.entity} · {a.action}</span>
                <span className="text-xs text-gray-500">{a.entity_id}</span>
                <span className="max-w-md truncate text-xs text-gray-500">{a.detail}</span>
                <span className="text-xs text-gray-400">{a.actor} · {a.created_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="p-8 text-center text-sm text-gray-500">Sin registros.</div>}
          </Card>
        )}
      </div>

      {/* note modal */}
      <Dialog open={!!noteModal} onOpenChange={(o) => !o && setNoteModal(null)}>
        <DialogContent data-testid="admin-note-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Nota de seguimiento</DialogTitle></DialogHeader>
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input"
            placeholder="Ej. Cliente pidió ajustar duración a 6 meses" />
          <button onClick={async () => {
            const q = quotes.find((x) => x.quote_id === noteModal);
            await setStatus(noteModal, q.status, note);
            setNoteModal(null);
          }} data-testid="admin-note-submit"
            className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue">Guardar nota</button>
        </DialogContent>
      </Dialog>

      {/* new space modal */}
      <Dialog open={!!newSpace} onOpenChange={(o) => !o && setNewSpace(null)}>
        <DialogContent data-testid="new-space-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Nuevo espacio publicitario</DialogTitle></DialogHeader>
          {newSpace && (
            <div className="space-y-3">
              <Row label="Retailer">
                <select value={newSpace.retailer_id} data-testid="new-space-retailer"
                  onChange={(e) => setNewSpace({ ...newSpace, retailer_id: e.target.value })}
                  className="h-10 w-full rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                  {retailers.map((r) => <option key={r.retailer_id} value={r.retailer_id}>{r.name}</option>)}
                </select>
              </Row>
              <Row label="Nombre"><Input value={newSpace.name} data-testid="new-space-name"
                onChange={(e) => setNewSpace({ ...newSpace, name: e.target.value })} /></Row>
              <Row label="Ciudad"><Input value={newSpace.city} data-testid="new-space-city"
                onChange={(e) => setNewSpace({ ...newSpace, city: e.target.value })} /></Row>
              <Row label="Formato"><Input value={newSpace.format} data-testid="new-space-format"
                onChange={(e) => setNewSpace({ ...newSpace, format: e.target.value })} /></Row>
              <Row label="Alcance mensual"><Input type="number" value={newSpace.reach_monthly}
                onChange={(e) => setNewSpace({ ...newSpace, reach_monthly: Number(e.target.value) })} /></Row>
              <Row label="Precio mensual (USD)"><Input type="number" value={newSpace.price_month}
                data-testid="new-space-price"
                onChange={(e) => setNewSpace({ ...newSpace, price_month: Number(e.target.value) })} /></Row>
              <Row label="Etapa del embudo">
                <select value={newSpace.funnel_stage || "conversion"} data-testid="new-space-stage"
                  onChange={(e) => setNewSpace({ ...newSpace, funnel_stage: e.target.value })}
                  className="h-10 w-full rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                  <option value="awareness">Awareness</option>
                  <option value="conversion">Conversión</option>
                </select>
              </Row>
              <button data-testid="new-space-submit" onClick={async () => {
                const r = retailers.find((x) => x.retailer_id === newSpace.retailer_id);
                const ok = await create("spaces", {
                  ...newSpace, retailer_name: r?.name || "", retailer_category: r?.category || "",
                  retailer_logo: r?.logo_url || "",
                });
                if (ok) setNewSpace(null);
              }} className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue">Crear espacio</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* new retailer modal */}
      <Dialog open={!!newRetailer} onOpenChange={(o) => !o && setNewRetailer(null)}>
        <DialogContent data-testid="new-retailer-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Nuevo retailer</DialogTitle></DialogHeader>
          {newRetailer && (
            <div className="space-y-3">
              <Row label="Nombre"><Input value={newRetailer.name} data-testid="new-retailer-name"
                onChange={(e) => setNewRetailer({ ...newRetailer, name: e.target.value })} /></Row>
              <Row label="Categoría"><Input value={newRetailer.category} data-testid="new-retailer-category"
                onChange={(e) => setNewRetailer({ ...newRetailer, category: e.target.value })} /></Row>
              <Row label="Iniciales (logo)"><Input value={newRetailer.logo_text}
                onChange={(e) => setNewRetailer({ ...newRetailer, logo_text: e.target.value })} /></Row>
              <Row label="URL del logo"><Input value={newRetailer.logo_url || ""} data-testid="new-retailer-logo"
                onChange={(e) => setNewRetailer({ ...newRetailer, logo_url: e.target.value })} /></Row>
              <Row label="Ciudades (coma)"><Input value={(newRetailer.cities || []).join(", ")}
                onChange={(e) => setNewRetailer({ ...newRetailer, cities: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></Row>
              <button data-testid="new-retailer-submit" onClick={async () => {
                if (await create("retailers", newRetailer)) setNewRetailer(null);
              }} className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue">Crear retailer</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* new service modal */}
      <Dialog open={!!newService} onOpenChange={(o) => !o && setNewService(null)}>
        <DialogContent data-testid="new-service-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Nuevo servicio adicional</DialogTitle></DialogHeader>
          {newService && (
            <div className="space-y-3">
              <Row label="Nombre"><Input value={newService.name} data-testid="new-service-name"
                onChange={(e) => setNewService({ ...newService, name: e.target.value })} /></Row>
              <Row label="Descripción"><Input value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })} /></Row>
              <Row label="Precio (USD)"><Input type="number" value={newService.price} data-testid="new-service-price"
                onChange={(e) => setNewService({ ...newService, price: Number(e.target.value) })} /></Row>
              <Row label="Tipo">
                <select value={newService.price_type}
                  onChange={(e) => setNewService({ ...newService, price_type: e.target.value })}
                  className="h-10 w-full rounded-lg border border-spott-mid px-3 text-sm text-spott-blue">
                  <option value="fixed">Fijo</option>
                  <option value="per_space">Por espacio</option>
                </select>
              </Row>
              <button data-testid="new-service-submit" onClick={async () => {
                if (await create("services", newService)) setNewService(null);
              }} className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue">Crear servicio</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
